// Offline phonetic transliteration (Latin -> Tamil / Malayalam / Hindi / Kannada / Telugu).
// Table-driven greedy longest-match; no network, no dependencies.

interface Script {
  virama: string;
  consonants: Record<string, string>;
  // independent vowel, dependent sign ('' = inherent 'a')
  vowels: Record<string, [string, string]>;
  extra?: Record<string, string>;
  // Hindi: a word-final bare consonant keeps its inherent 'a' (no virama)
  schwaEnd?: boolean;
}

const TAMIL: Script = {
  virama: '்',
  consonants: {
    k: 'க', g: 'க', kh: 'க', gh: 'க', ng: 'ங', ch: 'ச', c: 'ச', s: 'ச', j: 'ஜ',
    nj: 'ஞ', ny: 'ஞ', t: 'ட', d: 'ட', T: 'ட', D: 'ட', th: 'த', dh: 'த',
    N: 'ண', n: 'ந', p: 'ப', b: 'ப', f: 'ஃப', m: 'ம', y: 'ய', r: 'ர', l: 'ல',
    v: 'வ', w: 'வ', zh: 'ழ', L: 'ள', R: 'ற', h: 'ஹ', sh: 'ஷ', x: 'க்ஸ', z: 'ஜ',
    q: 'க', ndr: 'ன்ற', nd: 'ந்த', nth: 'ந்த', S: 'ஸ', C: 'ச'
  },
  vowels: {
    a: ['அ', ''], aa: ['ஆ', 'ா'], A: ['ஆ', 'ா'], i: ['இ', 'ி'], ii: ['ஈ', 'ீ'],
    ee: ['ஈ', 'ீ'], I: ['ஈ', 'ீ'], u: ['உ', 'ு'], uu: ['ஊ', 'ூ'], oo: ['ஊ', 'ூ'],
    U: ['ஊ', 'ூ'], e: ['எ', 'ெ'], E: ['ஏ', 'ே'], ai: ['ஐ', 'ை'], o: ['ஒ', 'ொ'],
    O: ['ஓ', 'ோ'], au: ['ஔ', 'ௌ']
  }
};

const MALAYALAM: Script = {
  virama: '്',
  consonants: {
    k: 'ക', kh: 'ഖ', g: 'ഗ', gh: 'ഘ', ng: 'ങ', ch: 'ച', c: 'ച', chh: 'ഛ', j: 'ജ',
    jh: 'ഝ', nj: 'ഞ', ny: 'ഞ', t: 'ട', T: 'ട', th: 'ത', d: 'ഡ', D: 'ഡ', dh: 'ദ',
    N: 'ണ', n: 'ന', p: 'പ', ph: 'ഫ', f: 'ഫ', b: 'ബ', bh: 'ഭ', m: 'മ', y: 'യ',
    r: 'ര', l: 'ല', v: 'വ', w: 'വ', sh: 'ശ', s: 'സ', h: 'ഹ', L: 'ള', zh: 'ഴ',
    R: 'റ', x: 'ക്സ', z: 'സ', q: 'ക'
  },
  vowels: {
    a: ['അ', ''], aa: ['ആ', 'ാ'], A: ['ആ', 'ാ'], i: ['ഇ', 'ി'], ii: ['ഈ', 'ീ'],
    ee: ['ഈ', 'ീ'], I: ['ഈ', 'ീ'], u: ['ഉ', 'ു'], uu: ['ഊ', 'ൂ'], oo: ['ഊ', 'ൂ'],
    U: ['ഊ', 'ൂ'], e: ['എ', 'െ'], E: ['ഏ', 'േ'], ai: ['ഐ', 'ൈ'], o: ['ഒ', 'ൊ'],
    O: ['ഓ', 'ോ'], au: ['ഔ', 'ൗ']
  }
};

const HINDI: Script = {
  virama: '्',
  schwaEnd: true,
  consonants: {
    k: 'क', kh: 'ख', g: 'ग', gh: 'घ', ng: 'ङ', ch: 'च', c: 'च', chh: 'छ', j: 'ज',
    jh: 'झ', ny: 'ञ', T: 'ट', Th: 'ठ', D: 'ड', Dh: 'ढ', N: 'ण', t: 'त', th: 'थ',
    d: 'द', dh: 'ध', n: 'न', p: 'प', ph: 'फ', f: 'फ़', b: 'ब', bh: 'भ', m: 'म',
    y: 'य', r: 'र', l: 'ल', L: 'ळ', v: 'व', w: 'व', sh: 'श', S: 'ष', s: 'स',
    h: 'ह', z: 'ज़', q: 'क़', x: 'क्स', ksh: 'क्ष', gy: 'ज्ञ', shr: 'श्र', tr: 'त्र'
  },
  vowels: {
    a: ['अ', ''], aa: ['आ', 'ा'], A: ['आ', 'ा'], i: ['इ', 'ि'], ii: ['ई', 'ी'],
    ee: ['ई', 'ी'], I: ['ई', 'ी'], u: ['उ', 'ु'], uu: ['ऊ', 'ू'], oo: ['ऊ', 'ू'],
    U: ['ऊ', 'ू'], e: ['ए', 'े'], E: ['ए', 'े'], ai: ['ऐ', 'ै'], o: ['ओ', 'ो'],
    O: ['ओ', 'ो'], au: ['औ', 'ौ']
  }
};

const KANNADA: Script = {
  virama: '್',
  consonants: {
    k: 'ಕ', kh: 'ಖ', g: 'ಗ', gh: 'ಘ', ng: 'ಙ', ch: 'ಚ', c: 'ಚ', chh: 'ಛ', j: 'ಜ',
    jh: 'ಝ', ny: 'ಞ', T: 'ಟ', Th: 'ಠ', D: 'ಡ', Dh: 'ಢ', N: 'ಣ', t: 'ತ', th: 'ಥ',
    d: 'ದ', dh: 'ಧ', n: 'ನ', p: 'ಪ', ph: 'ಫ', f: 'ಫ', b: 'ಬ', bh: 'ಭ', m: 'ಮ',
    y: 'ಯ', r: 'ರ', R: 'ಱ', l: 'ಲ', L: 'ಳ', v: 'ವ', w: 'ವ', sh: 'ಶ', S: 'ಷ', s: 'ಸ',
    h: 'ಹ', z: 'ಜ', x: 'ಕ್ಸ', q: 'ಕ', ksh: 'ಕ್ಷ', gny: 'ಜ್ಞ'
  },
  vowels: {
    a: ['ಅ', ''], aa: ['ಆ', 'ಾ'], A: ['ಆ', 'ಾ'], i: ['ಇ', 'ಿ'], ii: ['ಈ', 'ೀ'],
    ee: ['ಈ', 'ೀ'], I: ['ಈ', 'ೀ'], u: ['ಉ', 'ು'], uu: ['ಊ', 'ೂ'], oo: ['ಊ', 'ೂ'],
    U: ['ಊ', 'ೂ'], e: ['ಎ', 'ೆ'], E: ['ಏ', 'ೇ'], ai: ['ಐ', 'ೈ'], o: ['ಒ', 'ೊ'],
    O: ['ಓ', 'ೋ'], au: ['ಔ', 'ೌ']
  }
};

const TELUGU: Script = {
  virama: '్',
  consonants: {
    k: 'క', kh: 'ఖ', g: 'గ', gh: 'ఘ', ng: 'ఙ', ch: 'చ', c: 'చ', chh: 'ఛ', j: 'జ',
    jh: 'ఝ', ny: 'ఞ', T: 'ట', Th: 'ఠ', D: 'డ', Dh: 'ఢ', N: 'ణ', t: 'త', th: 'థ',
    d: 'ద', dh: 'ధ', n: 'న', p: 'ప', ph: 'ఫ', f: 'ఫ', b: 'బ', bh: 'భ', m: 'మ',
    y: 'య', r: 'ర', R: 'ఱ', l: 'ల', L: 'ళ', v: 'వ', w: 'వ', sh: 'శ', S: 'ష', s: 'స',
    h: 'హ', z: 'జ', x: 'క్స', q: 'క', ksh: 'క్ష', gny: 'జ్ఞ'
  },
  vowels: {
    a: ['అ', ''], aa: ['ఆ', 'ా'], A: ['ఆ', 'ా'], i: ['ఇ', 'ి'], ii: ['ఈ', 'ీ'],
    ee: ['ఈ', 'ీ'], I: ['ఈ', 'ీ'], u: ['ఉ', 'ు'], uu: ['ఊ', 'ూ'], oo: ['ఊ', 'ూ'],
    U: ['ఊ', 'ూ'], e: ['ఎ', 'ె'], E: ['ఏ', 'ే'], ai: ['ఐ', 'ై'], o: ['ఒ', 'ొ'],
    O: ['ఓ', 'ో'], au: ['ఔ', 'ౌ']
  }
};

const SCRIPTS: Record<string, Script> = {
  ta: TAMIL, ml: MALAYALAM, hi: HINDI, kn: KANNADA, te: TELUGU
};

export const isPhoneticLang = (lang?: string): boolean => !!lang && lang in SCRIPTS;

const longest = (map: Record<string, unknown>, s: string, i: number): string | null => {
  for (let len = 3; len >= 1; len--) {
    const key = s.substr(i, len);
    if (key.length === len && key in map) return key;
  }
  return null;
};

/** Convert a single Latin word (letters only) to the target script. */
function convertWord(word: string, script: Script): string {
  let out = '';
  let i = 0;
  let pendingConsonant = false; // last emitted char is a bare consonant (no vowel yet)

  while (i < word.length) {
    // Unknown capitals fall back to their lowercase mapping (e.g. "Chennai")
    const lower = word.toLowerCase();
    const pick = (map: Record<string, unknown>) => {
      const x = longest(map, word, i);
      const y = longest(map, lower, i);
      return x && (!y || x.length >= y.length) ? x : y;
    };
    const v = pick(script.vowels);
    const c = pick(script.consonants);

    // Prefer consonant when the longer match is a consonant (e.g. "ng", "th", "zh")
    if (c && (!v || c.length > v.length)) {
      if (pendingConsonant) out += script.virama;
      // Tamil: medial 'n' (between vowels) is ன, word-initial is ந
      out += script === TAMIL && c === 'n' && out && !pendingConsonant ? 'ன' : script.consonants[c];
      pendingConsonant = true;
      i += c.length;
    } else if (v) {
      const [indep, sign] = script.vowels[v];
      if (pendingConsonant) {
        out += sign; // inherent 'a' => ''
        pendingConsonant = false;
      } else {
        out += indep;
      }
      i += v.length;
    } else {
      // unknown letter: keep as is
      if (pendingConsonant) {
        out += script.virama;
        pendingConsonant = false;
      }
      out += word[i];
      i += 1;
    }
  }
  if (pendingConsonant && !script.schwaEnd) out += script.virama;
  return out;
}

/** Convert every Latin word inside `text`; non-Latin text/punctuation is untouched. */
export function transliterate(text: string, lang: string): string {
  const script = SCRIPTS[lang];
  if (!script || !text) return text;
  // Only pure-letter words are converted; tokens containing digits (GSTIN, PAN,
  // room/ID numbers, e-mail local parts like "abc123") are left untouched.
  return text.replace(/[A-Za-z0-9]+/g, (w) =>
    /^[A-Za-z]+$/.test(w) ? convertWord(w, script) : w
  );
}

const WORD_END = /[\s.,;:!?()/\\\-]/;

/**
 * Incremental helper: called with the full field value after each change.
 * Converts the Latin word that was just finished (value ends with a word
 * separator) so the caret never jumps mid-word. Pass `flush` on blur to
 * convert a trailing word as well.
 */
export function transliterateOnWordEnd(value: string, lang: string, flush = false): string {
  if (!isPhoneticLang(lang) || !value) return value;
  if (flush) return transliterate(value, lang);
  if (!WORD_END.test(value[value.length - 1])) return value;
  return transliterate(value, lang);
}

// ---------------------------------------------------------------------------
// Suggestions (offline): spelling variants of the phonetic conversion plus
// words the user picked before.
// ---------------------------------------------------------------------------

const VOWEL_ALT: Record<string, string[]> = {
  a: ['aa'], aa: ['a'], A: ['a'], i: ['ii'], ii: ['i'], ee: ['i'], I: ['i'],
  u: ['uu'], uu: ['u'], oo: ['u'], U: ['u'], e: ['E'], E: ['e'], o: ['O'], O: ['o']
};

const CONSONANT_ALT: Record<string, Record<string, string[]>> = {
  ta: {
    t: ['th'], th: ['t'], d: ['dh'], dh: ['d'], n: ['N'], N: ['n'],
    l: ['L', 'zh'], L: ['l', 'zh'], zh: ['l', 'L'], r: ['R'], R: ['r'],
    s: ['sh'], sh: ['s'], g: ['k'], b: ['p']
  },
  ml: {
    t: ['th'], th: ['t'], d: ['dh'], dh: ['d'], n: ['N'], N: ['n'],
    l: ['L', 'zh'], L: ['l', 'zh'], zh: ['l', 'L'], r: ['R'], R: ['r'],
    s: ['sh'], sh: ['s']
  },
  hi: {
    t: ['T'], T: ['t'], th: ['Th'], Th: ['th'], d: ['D'], D: ['d'], dh: ['Dh'],
    Dh: ['dh'], n: ['N'], N: ['n'], s: ['sh', 'S'], sh: ['s', 'S'], S: ['sh'],
    c: ['ch'], ch: ['chh']
  },
  kn: {
    t: ['T'], T: ['t'], th: ['Th'], Th: ['th'], d: ['D'], D: ['d'], dh: ['Dh'],
    Dh: ['dh'], n: ['N'], N: ['n'], l: ['L'], L: ['l'], r: ['R'], R: ['r'],
    s: ['sh', 'S'], sh: ['s', 'S'], S: ['sh']
  },
  te: {
    t: ['T'], T: ['t'], th: ['Th'], Th: ['th'], d: ['D'], D: ['d'], dh: ['Dh'],
    Dh: ['dh'], n: ['N'], N: ['n'], l: ['L'], L: ['l'], r: ['R'], R: ['r'],
    s: ['sh', 'S'], sh: ['s', 'S'], S: ['sh']
  }
};

const CHILLU: Record<string, string> = { 'ന്': 'ൻ', 'ല്': 'ൽ', 'ര്': 'ർ', 'ള്': 'ൾ', 'ണ്': 'ൺ' };

const MAX_SUGGESTIONS = 6;

/** Split a Latin word into the same tokens convertWord would match. */
function tokenize(word: string, script: Script): string[] {
  const lower = word.toLowerCase();
  const tokens: string[] = [];
  let i = 0;
  while (i < word.length) {
    const pick = (map: Record<string, unknown>) => {
      const x = longest(map, word, i);
      const y = longest(map, lower, i);
      return x && (!y || x.length >= y.length) ? x : y;
    };
    const v = pick(script.vowels);
    const c = pick(script.consonants);
    const tok = c && (!v || c.length > v.length) ? c : v;
    const len = tok ? tok.length : 1;
    tokens.push(word.substr(i, len));
    i += len;
  }
  return tokens;
}

// ---- learned picks (platform hooks persist these) ----
type Learned = Record<string, Record<string, string[]>>;
let learned: Learned = {};
const learnedListeners = new Set<(d: Learned) => void>();
const MAX_LEARNED_KEYS = 500;

export const hydrateLearned = (data: Learned | null | undefined): void => {
  if (data && typeof data === 'object') learned = data;
};
export const onLearnedChange = (cb: (d: Learned) => void): (() => void) => {
  learnedListeners.add(cb);
  return () => {
    learnedListeners.delete(cb);
  };
};

/** Remember that `latin` was completed as `picked` (most recent first). */
export function recordPick(lang: string, latin: string, picked: string): void {
  if (!isPhoneticLang(lang) || !latin || !picked || picked === latin) return;
  const key = latin.toLowerCase();
  const byLang = (learned[lang] = learned[lang] || {});
  const list = [picked, ...(byLang[key] || []).filter((p) => p !== picked)].slice(0, 3);
  delete byLang[key];
  byLang[key] = list; // re-insert => newest key last
  const keys = Object.keys(byLang);
  if (keys.length > MAX_LEARNED_KEYS) keys.slice(0, keys.length - MAX_LEARNED_KEYS).forEach((k) => delete byLang[k]);
  learnedListeners.forEach((l) => l(learned));
}

/**
 * Candidates for one Latin word: learned picks, the primary conversion,
 * spelling variants, and finally the word as typed. Empty for non-words.
 */
export function suggest(word: string, lang: string): string[] {
  const script = SCRIPTS[lang];
  if (!script || !word || !/^[A-Za-z]+$/.test(word)) return [];

  const out: string[] = [];
  const add = (s: string) => {
    if (s && s !== word && !out.includes(s)) out.push(s);
  };

  (learned[lang]?.[word.toLowerCase()] || []).forEach(add);

  const primary = convertWord(word, script);
  add(primary);

  if (lang === 'ta') {
    const flip = primary.replace(/[னந]/, (m) => (m === 'ன' ? 'ந' : 'ன'));
    add(flip);
  }
  if (lang === 'ml') {
    const last = primary.slice(-2);
    if (CHILLU[last]) add(primary.slice(0, -2) + CHILLU[last]);
  }

  const tokens = tokenize(word, script);
  const alts: Array<[number, string]> = [];
  tokens.forEach((t, idx) => {
    const list = VOWEL_ALT[t] || CONSONANT_ALT[lang]?.[t] || [];
    list.forEach((a) => alts.push([idx, a]));
  });

  const build = (changes: Array<[number, string]>) => {
    const copy = tokens.slice();
    changes.forEach(([idx, a]) => (copy[idx] = a));
    return convertWord(copy.join(''), script);
  };

  // one change, then two changes (bounded), fewest changes first
  alts.forEach((a) => add(build([a])));
  for (let x = 0; x < alts.length && out.length < 12; x++) {
    for (let y = x + 1; y < alts.length && out.length < 12; y++) {
      if (alts[x][0] !== alts[y][0]) add(build([alts[x], alts[y]]));
    }
  }

  return [...out.slice(0, MAX_SUGGESTIONS - 1), word];
}

/** The Latin word right before the caret (null when none / part of a token with digits). */
export function splitTrailingWord(
  value: string,
  caret: number
): { head: string; word: string; tail: string } | null {
  const before = value.slice(0, caret);
  const tail = value.slice(caret);
  if (/^[A-Za-z0-9]/.test(tail)) return null; // caret is mid-word
  const m = /[A-Za-z]+$/.exec(before);
  if (!m) return null;
  const head = before.slice(0, before.length - m[0].length);
  if (/[0-9]$/.test(head)) return null; // part of an ID/number token
  return { head, word: m[0], tail };
}

/**
 * Like transliterateOnWordEnd but converts only the Latin word at the end of
 * the value (before a just-typed separator, or at the very end when `flush`).
 * Earlier Latin words are left alone so a deliberate "keep English" pick sticks.
 */
export function transliterateTrailing(value: string, lang: string, flush = false): string {
  if (!isPhoneticLang(lang) || !value) return value;
  const m = flush
    ? /(^|[^A-Za-z0-9])([A-Za-z]+)$/.exec(value)
    : /(^|[^A-Za-z0-9])([A-Za-z]+)([\s.,;:!?()/\-])$/.exec(value);
  if (!m) return value;
  const word = m[2];
  const start = m.index + m[1].length;
  const conv = convertWord(word, SCRIPTS[lang]);
  return value.slice(0, start) + conv + value.slice(start + word.length);
}
